import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type { SxProps, Theme } from '@mui/material/styles';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Shown in the list but not selectable — e.g. a line still awaiting QC. */
  disabled?: boolean;
}

interface SearchableSelectProps {
  label: string;
  value: string;
  options: SearchableSelectOption[];
  onChange: (value: string) => void;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  helperText?: string;
  /** Renders the field in its error state, same as `<TextField error>` — pair it with helperText
   * carrying the message, exactly how a react-hook-form fieldState is normally surfaced. */
  error?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

/** Drop-in replacement for a `<TextField select>` record picker (Material/Warehouse/Supplier/
 * Customer/Purchase Order/User/...) — type-to-filter via MUI Autocomplete instead of scrolling a
 * long native dropdown. Not meant for small fixed-value enums (Status, Picking Strategy, Timing,
 * ...), which stay plain `<TextField select>` since a search box adds nothing for 2-6 options. */
export default function SearchableSelect({ label, value, options, onChange, fullWidth, size = 'medium', helperText, error, disabled, sx }: SearchableSelectProps) {
  const selected = options.find((o) => o.value === value) ?? null;

  return (
    <Autocomplete
      value={selected}
      onChange={(_, next) => onChange(next?.value ?? '')}
      options={options}
      getOptionLabel={(o) => o.label}
      getOptionDisabled={(o) => Boolean(o.disabled)}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      fullWidth={fullWidth}
      size={size}
      disabled={disabled}
      sx={sx}
      renderInput={(params) => <TextField {...params} label={label} helperText={helperText} error={error} />}
    />
  );
}
